import React from 'react';
import { Button } from "@/components/ui/button";
import { BookOpen, Users, Award, ArrowRight } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LoginForm } from "@/components/auth/LoginForm";
import { TrialForm } from "@/components/auth/TrialForm";
import edugagyLogo from '@/assets/edugogy-logo.png';
import { useAuth } from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';

export default function Home() {
  const { user, loading } = useAuth();

  // Redirect to dashboard if already logged in
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="light min-h-screen bg-gradient-to-br from-background to-muted/50">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center space-y-8">
          <div className="space-y-4">
            <div className="flex justify-center mb-4">
              <img 
                src={edugagyLogo} 
                alt="Edugogy Platform Logo" 
                className="h-84 md:h-120 lg:h-144 xl:h-[42rem] w-auto object-contain max-w-full"
              />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-primary bg-clip-text text-transparent">
              Professional Learning Made Simple
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Discover, learn, and grow with comprehensive professional development courses designed for educators.
            </p>
          </div>

          {/* Authentication Tabs */}
          <div className="max-w-md mx-auto">
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">Sign In</TabsTrigger>
                <TabsTrigger value="trial">Request Trial</TabsTrigger>
              </TabsList>
              <TabsContent value="login" className="mt-6">
                <LoginForm />
              </TabsContent>
              <TabsContent value="trial" className="mt-6">
                <TrialForm />
              </TabsContent>
            </Tabs>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto mt-16">
            <div className="text-center space-y-4 p-6 rounded-lg bg-gradient-card border border-primary/10">
              <div className="bg-gradient-primary w-16 h-16 rounded-full flex items-center justify-center mx-auto shadow-lg">
                <BookOpen className="h-8 w-8 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">Quality Courses</h3>
              <p className="text-muted-foreground">
                Access a wide range of professional development courses designed for educators.
              </p>
            </div>

            <div className="text-center space-y-4 p-6 rounded-lg bg-gradient-card border border-primary/10">
              <div className="bg-gradient-success w-16 h-16 rounded-full flex items-center justify-center mx-auto shadow-lg">
                <Users className="h-8 w-8 text-success-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">Expert Instructors</h3>
              <p className="text-muted-foreground">
                Learn from experienced professionals and industry experts.
              </p>
            </div>

            <div className="text-center space-y-4 p-6 rounded-lg bg-gradient-card border border-primary/10">
              <div className="bg-gradient-primary w-16 h-16 rounded-full flex items-center justify-center mx-auto shadow-lg">
                <Award className="h-8 w-8 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">Professional Development</h3>
              <p className="text-muted-foreground">
                Earn PD hours and advance your career with recognized certifications.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}